import { useForm } from "react-hook-form";
import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";

import { notify } from "@/lib/notify";
import { authApi } from "@/lib/api/auth";
import { useAuthStore } from "@/stores/auth-store";
import { Button } from "@/components/ui/Button";
import { PasswordInput } from "@/components/ui/Input";
import { ErrorMessage } from "@/components/ui/ErrorMessage";

interface Values {
  current: string;
  password: string;
  confirm: string;
}

/**
 * Shown instead of the whole app while the signed-in account still has the
 * temporary password an admin issued (user.must_change_password). The server
 * enforces the same rule - every non-/auth request answers
 * PASSWORD_CHANGE_REQUIRED - so this screen is the only way forward.
 */
export function ForcePasswordChangePage() {
  const user = useAuthStore((s) => s.user);
  const signOut = useAuthStore((s) => s.signOut);
  const navigate = useNavigate();
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<Values>();

  const mutation = useMutation({
    meta: { silent: true },
    mutationFn: (values: Values) => authApi.changePassword(values.current, values.password),
    onSuccess: () => {
      // Changing the password ends every session, this one included.
      notify.success("Password changed. Sign in with your new password.", 5000);
      signOut();
      navigate("/login", { replace: true });
    },
  });

  function leave() {
    authApi.logout().catch(() => undefined);
    signOut();
    navigate("/login", { replace: true });
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-brand px-4">
      <div className="w-full max-w-sm">
        <div className="mb-8 text-center">
          <h1 className="text-xl font-semibold text-white">Set a new password</h1>
          <p className="mt-1 text-sm text-primary-200">
            {user?.first_name ? `${user.first_name}, you` : "You"} signed in with a temporary password. Choose your own
            to continue.
          </p>
        </div>

        <div className="rounded-2xl bg-white p-6 shadow-2xl">
          <form
            onSubmit={handleSubmit((values) => {
              if (values.password !== values.confirm) {
                setError("confirm", { message: "Passwords do not match" });
                return;
              }
              if (values.password === values.current) {
                setError("password", { message: "Choose a password different from the temporary one" });
                return;
              }
              mutation.mutate(values);
            })}
            className="flex flex-col gap-4"
          >
            <PasswordInput
              label="Temporary password"
              autoComplete="current-password"
              error={errors.current?.message}
              {...register("current", { required: "Enter the temporary password you were given" })}
            />
            <PasswordInput
              label="New password"
              autoComplete="new-password"
              hint="At least 10 characters, with uppercase, lowercase, a number and a special character."
              error={errors.password?.message}
              {...register("password", {
                required: "Enter a new password",
                minLength: { value: 10, message: "Use at least 10 characters" },
              })}
            />
            <PasswordInput
              label="Confirm new password"
              autoComplete="new-password"
              error={errors.confirm?.message}
              {...register("confirm", { required: "Confirm the new password" })}
            />
            {mutation.isError && <ErrorMessage error={mutation.error} />}
            <Button type="submit" loading={mutation.isPending} fullWidth className="mt-1">
              Change password
            </Button>
          </form>
        </div>

        <p className="mt-6 text-center text-xs text-primary-200">
          Not you?{" "}
          <button type="button" onClick={leave} className="underline">
            Sign out
          </button>
        </p>
      </div>
    </div>
  );
}
